import type {Component, Editor} from 'grapesjs';
import type {FieldMeta} from '@FormMakerReact/form-maker.types';
import {BIRTHDAY_FORMATS, DEFAULT_FIELDS_META, PMW_CUSTOM_INPUT} from '@FormMakerReact/form-maker.types';
import {POSTERMYWALL_EMAIL_BOX_TYPE} from '@FormMakerReact/components/postermywall-email-box/postermywall-email-box';
import {POSTERMYWALL_FIRST_NAME_BOX_TYPE} from '@FormMakerReact/components/postermywall-first-name-box/postermywall-first-name-box';
import {POSTERMYWALL_LAST_NAME_BOX_TYPE} from '@FormMakerReact/components/postermywall-last-name-box/postermywall-last-name-box';
import {POSTERMYWALL_PHONE_BOX_TYPE} from '@FormMakerReact/components/postermywall-phone-box/postermywall-phone-box';
import {POSTERMYWALL_BIRTHDAY_BOX_TYPE} from '@FormMakerReact/components/postermywall-birthday-box/postermywall-birthday-box';
import {POSTERMYWALL_ADDRESS_BOX_TYPE} from '@FormMakerReact/components/postermywall-address-box/postermywall-address-box';
import {POSTERMYWALL_CUSTOM_BOX_TYPE_PREFIX} from '@FormMakerReact/components/postermywall-custom-box/postermywall-custom-box';
import {POSTERMYWALL_EMAIL_FORM_TYPE} from '@FormMakerReact/components/postermywall-email-form/postermywall-email-form';
import {MAX_CUSTOM_FIELDS} from './form-maker-fields-mutator-library';

function mergeWithDefaults(serverFieldsMeta: FieldMeta[]): FieldMeta[] {
  if (serverFieldsMeta.length === 0) {
    return DEFAULT_FIELDS_META.map((f) => ({...f}));
  }

  const hasEmail = serverFieldsMeta.some((f) => f.component === 'postermywall-email-input');
  if (!hasEmail) {
    return [...DEFAULT_FIELDS_META.map((f) => ({...f})), ...serverFieldsMeta];
  }

  return serverFieldsMeta;
}

/**
 * Renumbers `order` to 1..length while preserving every other field as-is.
 * `customFieldIndex` is intentionally NOT renumbered — it must mirror the
 * `postermywall-custom-box-N` index already on canvas, otherwise the toggle
 * list resolves the wrong label and a save-reload writes the wrong label back.
 */
export function dedupeAndRenormalize(fieldsMeta: FieldMeta[]): FieldMeta[] {
  return fieldsMeta.map((field, index) => ({...field, order: index + 1}));
}

export function normalizeLoadedFieldsMeta(serverFieldsMeta: unknown): FieldMeta[] {
  const safe = toFieldMetaArray(serverFieldsMeta);
  const merged = mergeWithDefaults(safe);
  return dedupeAndRenormalize(merged);
}

/** Overlays idUserHasCustomField from a save response onto the editor's fieldsMeta state without touching other props. */
export function mergeIdUserHasCustomFieldFromResponse(state: readonly FieldMeta[], response: readonly FieldMeta[]): FieldMeta[] {
  const responseIdByCustomFieldIndex = new Map<number, number>();
  response.forEach((entry) => {
    if (entry.customFieldIndex !== undefined && entry.idUserHasCustomField !== undefined) {
      responseIdByCustomFieldIndex.set(entry.customFieldIndex, entry.idUserHasCustomField);
    }
  });
  if (responseIdByCustomFieldIndex.size === 0) {
    return state.slice();
  }
  return state.map((f) => {
    if (f.customFieldIndex === undefined) {
      return f;
    }
    const responseId = responseIdByCustomFieldIndex.get(f.customFieldIndex);
    if (responseId === undefined || responseId === f.idUserHasCustomField) {
      return f;
    }
    return {...f, idUserHasCustomField: responseId};
  });
}

/**
 * Appends each fieldsMeta custom whose customFieldIndex isn't already on
 * canvas. Toggled-off customs keep their registry slot so they can be flipped
 * back on; the save payload must carry them forward.
 */
function appendOffCanvasCustomsFromMeta(result: FieldMeta[], customMetaByIndex: ReadonlyMap<number, FieldMeta>): void {
  const indicesOnCanvas = new Set<number>();
  result.forEach((entry) => {
    if (entry.component === PMW_CUSTOM_INPUT && entry.customFieldIndex !== undefined) {
      indicesOnCanvas.add(entry.customFieldIndex);
    }
  });
  customMetaByIndex.forEach((entry, customIndex) => {
    if (indicesOnCanvas.has(customIndex)) {
      return;
    }
    result.push({
      component: PMW_CUSTOM_INPUT,
      order: result.length + 1,
      label: entry.label,
      format: entry.format,
      required: entry.required,
      customFieldIndex: customIndex,
      idUserHasCustomField: entry.idUserHasCustomField,
    });
  });
}

function toFieldMetaArray(value: unknown): FieldMeta[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(isFieldMetaEntry);
}

function isFieldMetaEntry(entry: unknown): entry is FieldMeta {
  if (typeof entry !== 'object' || entry === null) {
    return false;
  }
  const e = entry as Record<string, unknown>;
  return typeof e.component === 'string' && typeof e.order === 'number' && typeof e.label === 'string' && typeof e.format === 'string';
}

interface StandardFieldDescriptor {
  component: string;
  defaultLabel: string;
  defaultFormat: string;
}

const STANDARD_BOX_TO_FIELD: readonly {boxType: string; descriptor: StandardFieldDescriptor}[] = [
  {
    boxType: POSTERMYWALL_EMAIL_BOX_TYPE,
    descriptor: {component: 'postermywall-email-input', defaultLabel: 'pmwjs_email', defaultFormat: 'email'},
  },
  {
    boxType: POSTERMYWALL_FIRST_NAME_BOX_TYPE,
    descriptor: {component: 'postermywall-first-name-input', defaultLabel: 'pmwjs_first_name', defaultFormat: 'string'},
  },
  {
    boxType: POSTERMYWALL_LAST_NAME_BOX_TYPE,
    descriptor: {component: 'postermywall-last-name-input', defaultLabel: 'pmwjs_last_name', defaultFormat: 'string'},
  },
  {
    boxType: POSTERMYWALL_PHONE_BOX_TYPE,
    descriptor: {component: 'postermywall-phone-input', defaultLabel: 'pmwjs_phone', defaultFormat: 'tel'},
  },
  {
    boxType: POSTERMYWALL_BIRTHDAY_BOX_TYPE,
    descriptor: {component: 'postermywall-birthday-input', defaultLabel: 'pmwjs_birthday', defaultFormat: BIRTHDAY_FORMATS.DMY},
  },
  {
    boxType: POSTERMYWALL_ADDRESS_BOX_TYPE,
    descriptor: {component: 'postermywall-address-input', defaultLabel: 'pmwjs_address', defaultFormat: 'string'},
  },
];

export function buildFieldsMetaFromCanvas(editor: Editor, currentFieldsMeta: readonly FieldMeta[]): FieldMeta[] {
  const wrapper = editor.getWrapper();
  if (!wrapper) {
    return currentFieldsMeta.slice();
  }

  const emailForms = wrapper.findType(POSTERMYWALL_EMAIL_FORM_TYPE);
  if (emailForms.length === 0) {
    return currentFieldsMeta.slice();
  }
  const emailForm = emailForms[0];
  const formChildren: Component[] = emailForm.components().models;

  const standardMetaByComponent = new Map<string, FieldMeta>();
  const customMetaByIndex = new Map<number, FieldMeta>();
  currentFieldsMeta.forEach((entry) => {
    if (entry.component === PMW_CUSTOM_INPUT && entry.customFieldIndex !== undefined) {
      customMetaByIndex.set(entry.customFieldIndex, entry);
    } else {
      standardMetaByComponent.set(entry.component, entry);
    }
  });

  const boxTypeToDescriptor = new Map<string, StandardFieldDescriptor>();
  STANDARD_BOX_TO_FIELD.forEach(({boxType, descriptor}) => {
    boxTypeToDescriptor.set(boxType, descriptor);
  });

  const result: FieldMeta[] = [];

  formChildren.forEach((child) => {
    const childType: string = child.get('type') ?? '';

    const descriptor = boxTypeToDescriptor.get(childType);
    if (descriptor !== undefined) {
      const existing = standardMetaByComponent.get(descriptor.component);
      result.push({
        component: descriptor.component,
        order: result.length + 1,
        label: existing?.label ?? descriptor.defaultLabel,
        format: existing?.format ?? descriptor.defaultFormat,
        required: existing?.required,
      });
      return;
    }

    if (childType.startsWith(POSTERMYWALL_CUSTOM_BOX_TYPE_PREFIX + '-')) {
      const suffix = childType.slice(POSTERMYWALL_CUSTOM_BOX_TYPE_PREFIX.length + 1);
      const customIndex = parseInt(suffix, 10);
      if (!isNaN(customIndex) && customIndex >= 1 && customIndex <= MAX_CUSTOM_FIELDS) {
        const existing = customMetaByIndex.get(customIndex);
        const defaultLabel = `${window.i18next.t('pmwjs_custom_field')} ${customIndex.toString()}`;
        result.push({
          component: PMW_CUSTOM_INPUT,
          order: result.length + 1,
          label: existing?.label ?? defaultLabel,
          format: existing?.format ?? 'string',
          required: existing?.required,
          customFieldIndex: customIndex,
          idUserHasCustomField: existing?.idUserHasCustomField,
        });
      }
    }
  });

  appendOffCanvasCustomsFromMeta(result, customMetaByIndex);
  return result;
}
